export type BackendSession = {
  accessToken: string;
  accessTokenExpiresAtUtc: string;
  refreshToken: string;
};
export type OtpStartResponse = {
  sent: boolean;
  expiresInSeconds: number;
  devOtp?: string | null;
};
export type OtpVerifyResponse = {
  tokens: BackendSession;
  isNewUser: boolean;
  profileRequired: boolean;
};

const ACCESS_TOKEN_KEY = "busiconn_access_token";
const REFRESH_TOKEN_KEY = "busiconn_refresh_token";
const ACCESS_TOKEN_EXPIRES_KEY = "busiconn_access_token_expires_at";
let refreshInFlight: Promise<BackendSession | null> | null = null;

export const backendBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8080").replace(/\/$/, "");

function normalizeBackendPayload<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(normalizeBackendPayload) as T;
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).map(([key, entry]) => [key, normalizeBackendPayload(entry)] as const);
    return Object.fromEntries(entries) as T;
  }

  if (typeof value === "string") {
    if (value.startsWith("/api/v1/media/") || value.startsWith("/uploads/")) {
      return `${backendBaseUrl}${value}` as T;
    }
  }

  return value;
}

export function getStoredAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getStoredRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getStoredAccessTokenExpiresAt() {
  return localStorage.getItem(ACCESS_TOKEN_EXPIRES_KEY);
}

export function setBackendSession(session: BackendSession) {
  localStorage.setItem(ACCESS_TOKEN_KEY, session.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
  localStorage.setItem(ACCESS_TOKEN_EXPIRES_KEY, session.accessTokenExpiresAtUtc);
}

export function clearBackendSession() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_EXPIRES_KEY);
}

export async function backendFetch(
  path: string,
  init: RequestInit = {},
  retryOnAuthFailure = true
): Promise<Response> {
  const send = async () => {
    const headers = new Headers(init.headers ?? {});
    const hasBody = init.body !== undefined && init.body !== null;
    if (hasBody && !(init.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }

    const token = getStoredAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return fetch(`${backendBaseUrl}${path}`, { ...init, headers });
  };

  const response = await send();

  if (response.status === 401 && retryOnAuthFailure && getStoredRefreshToken()) {
    const refreshed = await refreshBackendSession();
    if (refreshed) {
      return send();
    }
  }

  return response;
}

export async function backendRequest<T>(
  path: string,
  init: RequestInit = {},
  retryOnAuthFailure = true
): Promise<T> {
  const response = await backendFetch(path, init, retryOnAuthFailure);

  if (!response.ok) {
    throw new Error(await readBackendError(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return normalizeBackendPayload(await response.json()) as T;
  }

  return (await response.text()) as T;
}

export async function registerBackendUser(input: {
  email: string;
  displayName: string;
  username: string;
  password: string;
  acceptedSla: boolean;
  imageUrl?: string | null;
}) {
  return await backendRequest<any>("/api/v1/auth/register", {
    method: "POST",
    body: JSON.stringify({
      email: input.email,
      displayName: input.displayName,
      username: input.username,
      password: input.password,
      acceptedSla: input.acceptedSla,
      imageUrl: input.imageUrl ?? null,
    }),
  });
}

export async function signInBackendUser(email: string, password: string, deviceName?: string) {
  const session = await backendRequest<BackendSession>("/api/v1/auth/sign-in", {
    method: "POST",
    body: JSON.stringify({ login: email, password, deviceName: deviceName ?? null }),
  });
  setBackendSession(session);
  return session;
}

export async function signInBackendOAuth(
  provider: string,
  token: string,
  deviceName?: string,
  email?: string | null,
  displayName?: string | null
) {
  const session = await backendRequest<BackendSession>("/api/v1/auth/oauth", {
    method: "POST",
    body: JSON.stringify({
      provider,
      token,
      deviceName: deviceName ?? navigator.userAgent.slice(0, 120),
      email: email ?? null,
      displayName: displayName ?? null,
    }),
  });
  setBackendSession(session);
  return session;
}

export async function passwordSignupBackendUser(phoneNumber: string, password: string, otp: string, deviceName?: string) {
  const result = await backendRequest<OtpVerifyResponse>("/api/v1/auth/password-sign-up", {
    method: "POST",
    body: JSON.stringify({ phoneNumber, password, otp, deviceName: deviceName ?? navigator.userAgent.slice(0, 120) }),
  });
  setBackendSession(result.tokens);
  return result;
}

export async function startPasswordSignupBackendUser(phoneNumber: string, password: string) {
  return await backendRequest<OtpStartResponse>("/api/v1/auth/password-sign-up/start", {
    method: "POST",
    body: JSON.stringify({ phoneNumber, password }),
  });
}

export async function startBackendOtp(phoneNumber: string) {
  return await backendRequest<OtpStartResponse>("/api/v1/auth/otp/start", {
    method: "POST",
    body: JSON.stringify({ phoneNumber }),
  });
}

export async function verifyBackendOtp(phoneNumber: string, otp: string, deviceName?: string) {
  const result = await backendRequest<OtpVerifyResponse>("/api/v1/auth/otp/verify", {
    method: "POST",
    body: JSON.stringify({ phoneNumber, otp, deviceName: deviceName ?? navigator.userAgent.slice(0, 120) }),
  });
  setBackendSession(result.tokens);
  return result;
}

export async function completeBackendProfile(displayName: string) {
  return await backendRequest<any>("/api/v1/users/me/profile", {
    method: "POST",
    body: JSON.stringify({ displayName }),
  });
}

export async function setBackendPassword(password: string) {
  return await backendRequest<void>("/api/v1/users/me/password", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function refreshBackendSession() {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return null;
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const session = await backendRequest<BackendSession>(
        "/api/v1/auth/refresh",
        {
          method: "POST",
          body: JSON.stringify({ refreshToken, deviceName: navigator.userAgent.slice(0, 120) }),
        },
        false
      );
      setBackendSession(session);
      return session;
    } catch (error) {
      // Network outages must not discard an otherwise valid long-lived session.
      if (error instanceof Error && /invalid|expired|revoked|unavailable/i.test(error.message)) {
        clearBackendSession();
      }
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function signOutBackendUser() {
  const refreshToken = getStoredRefreshToken();
  if (refreshToken) {
    try {
      await backendRequest(
        "/api/v1/auth/revoke",
        {
          method: "POST",
          body: JSON.stringify({ refreshToken }),
        },
        false
      );
    } catch {
      // Ignore revoke failures during sign-out.
    }
  }

  clearBackendSession();
}

export async function getBackendCurrentUser() {
  if (!getStoredAccessToken()) {
    return null;
  }
  return await backendRequest<any>("/api/v1/users/me");
}

export async function getBackendUserById(userId: string) {
  return await backendRequest<any>(`/api/v1/users/${userId}`);
}

export async function getBackendUsersByIds(userIds: string[]) {
  return await backendRequest<any[]>("/api/v1/users/by-ids", {
    method: "POST",
    body: JSON.stringify({ userIds }),
  });
}

async function readBackendError(response: Response) {
  const fallback = `Request failed with status ${response.status}`;
  const text = await response.text().catch(() => "");
  if (!text) {
    return fallback;
  }

  try {
    const problem = JSON.parse(text);
    const knownMessage = friendlyBackendError(problem.title);
    if (knownMessage) {
      return knownMessage;
    }

    if (problem.errors && typeof problem.errors === "object") {
      const firstError = Object.values(problem.errors).flat().find(Boolean);
      if (typeof firstError === "string") {
        return firstError;
      }
    }

    if (typeof problem.detail === "string" && problem.detail.trim()) {
      return problem.detail;
    }

    if (typeof problem.error === "string" && problem.error.trim()) {
      return problem.error;
    }

    if (typeof problem.title === "string" && problem.title.trim()) {
      return problem.title;
    }
  } catch {
    // Non-JSON responses fall through to a short generic error.
  }

  return fallback;
}

function friendlyBackendError(code?: string) {
  switch (code) {
    case "identity.invalid_credentials":
      return "Incorrect phone number or password.";
    case "identity.user_not_found":
      return "No account found for this number. Create an account or use OTP login to continue.";
    case "identity.phone_taken":
      return "This phone number already has an account. Please login or use OTP.";
    case "identity.invalid_otp":
      return "That OTP is incorrect or expired. Please try again.";
    case "identity.invalid_phone":
      return "Enter a valid 10 digit mobile number.";
    case "identity.invalid_password":
      return "Password must be 8 to 128 characters.";
    case "identity.password_login_not_enabled":
      return "This account was created with OTP login. Please sign in with OTP or set a password first.";
    case "identity.account_unavailable":
      return "This account is unavailable.";
    default:
      return null;
  }
}
